import {
  LaptopOutlined,
  LogoutOutlined,
  MoonOutlined,
  SunOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { AdminUser } from "@features/users/api";
import { getPrimaryContact } from "@shared/directoryUsers";
import { clearStoredUser } from "@shared/userStorage";
import { Avatar, Dropdown, type MenuProps, Space, Typography } from "antd";
import { useMemo } from "react";
import { useNavigate } from "react-router";

type ThemeMode = "light" | "dark" | "system";

interface ProfileMenuProps {
  user?: AdminUser;
  mode: ThemeMode;
  onModeChange: (mode: ThemeMode) => void;
}

const ProfileMenu = ({ user, mode, onModeChange }: ProfileMenuProps) => {
  const navigate = useNavigate();
  const initials = useMemo(() => {
    const parts = [user?.last_name, user?.first_name]
      .map((p) => (p ? p.trim().charAt(0).toUpperCase() : ""))
      .filter(Boolean);
    return parts.slice(0, 2).join("") || "U";
  }, [user]);

  const email = getPrimaryContact(user, "email") ?? "—";
  const fullName =
    [user?.first_name, user?.last_name]
      .map((p) => p?.trim())
      .filter(Boolean)
      .join(" ") || "Профиль";

  const isActive = (key: ThemeMode) => mode === key;
  const activeDot = (
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: "#1677ff",
        display: "inline-block",
      }}
    />
  );

  const items: MenuProps["items"] = [
    {
      key: "info",
      disabled: true,
      label: (
        <Space orientation="vertical" size={4}>
          <Typography.Text strong>{fullName}</Typography.Text>
          <Typography.Text type="secondary" style={{ display: "block" }}>
            {email}
          </Typography.Text>
        </Space>
      ),
    },
    { type: "divider" },
    {
      key: "theme_system",
      icon: <LaptopOutlined />,
      label: (
        <Space style={{ justifyContent: "space-between", width: "100%" }}>
          Системная тема
          {isActive("system") ? activeDot : null}
        </Space>
      ),
      onClick: () => onModeChange("system"),
    },
    {
      key: "theme_light",
      icon: <SunOutlined />,
      label: (
        <Space style={{ justifyContent: "space-between", width: "100%" }}>
          Светлая
          {isActive("light") ? activeDot : null}
        </Space>
      ),
      onClick: () => onModeChange("light"),
    },
    {
      key: "theme_dark",
      icon: <MoonOutlined />,
      label: (
        <Space style={{ justifyContent: "space-between", width: "100%" }}>
          Тёмная
          {isActive("dark") ? activeDot : null}
        </Space>
      ),
      onClick: () => onModeChange("dark"),
    },
    { type: "divider" },
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "Профиль",
      onClick: () => navigate("/settings/profile"),
    },
    { type: "divider" },
    {
      key: "logout",
      danger: true,
      label: "Выйти",
      icon: <LogoutOutlined />,
      onClick: () => {
        localStorage.removeItem("acweb_access");
        localStorage.removeItem("acweb_refresh");
        clearStoredUser();
        navigate("/auth/login");
      },
    },
  ];

  const avatarUrl = typeof user?.avatar_url === "string" ? user.avatar_url : undefined;

  return (
    <Dropdown menu={{ items }} placement="bottomRight" trigger={["click"]}>
      <Avatar
        shape="circle"
        style={{ cursor: "pointer", backgroundColor: "#334155" }}
        src={avatarUrl}
      >
        {initials}
      </Avatar>
    </Dropdown>
  );
};

export default ProfileMenu;
